'use client';

import { useEffect, useState, useRef } from "react";
import { useRouter, usePathname } from "next/navigation";
import { signIn, signOut } from "next-auth/react";
import { clearNatsPending } from "@/app/actions/pending";

const SLOW_THRESHOLD_MS = 10_000;

const provider = process.env.NODE_ENV === `production` ? `mailgun` : `nodemailer`;

export default function PendingClient({ email, domain, }: { email: string | null | undefined; domain: string }) {
  const router = useRouter();
  const pathname = usePathname();
  const [elapsed, setElapsed,] = useState(0);
  const [status, setStatus,] = useState<string | null>(null);
  const [stopped, setStopped,] = useState(false);
  const stoppedRef = useRef(false);

  const handleRetry = async () => {
    stoppedRef.current = true;
    setStopped(true);
    await clearNatsPending();
    await signOut({ redirect: false, });
    await signIn(provider, { email: email ?? ``, callbackUrl: pathname, });
  };

  useEffect(() => {
    if (!email) {
      router.push(`/`);
      return;
    }

    if (stoppedRef.current) return;

    const start = Date.now();
    let timeoutId: ReturnType<typeof setTimeout>;

    const intervalId = setInterval(() => {
      setElapsed(Date.now() - start);
    }, 1000);

    const checkIfPending = async () => {
      if (stoppedRef.current) return;
      try {
        const res = await fetch(`/api/pending?email=${email}`);
        const result = await res.json();

        if (result.data) {
          setStatus(result.data);
          timeoutId = setTimeout(checkIfPending, 500);
        } else {
          router.refresh();
        }
      } catch (error) {
        console.error(`Polling failed`);
        timeoutId = setTimeout(checkIfPending, 500);
      }
    };

    checkIfPending();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [router, email, stopped,]);

  if (status === `error`) {
    return (
      <>
        <h1>Something went wrong</h1>
        <p>We were unable to verify your account.</p>
        <a className={`cursor-pointer`} onClick={handleRetry}>Retry Login</a>
        <span> or </span>
        <a href={`mailto:contact@${domain}`}>Contact Us</a>
      </>
    );
  }

  if (elapsed >= SLOW_THRESHOLD_MS) {
    return (
      <>
        <h1>Please wait</h1>
        <p>This is taking longer than expected...</p>
        <a href={`mailto:contact@${domain}`}>Contact us</a>
      </>
    );
  }

  return (
    <>
      <h1>Please wait</h1>
      <p>We are checking your account...</p>
      <a href={`mailto:contact@${domain}`}>Contact us</a>
    </>
  );
}
